<template>
  <ColumnChart
    :request-status="requestStatus"
    :categories="categories"
    :series="series"
    :title="title"
    :item-id="itemId"
    :card-options="cardOptions"
    :show-granularity="false"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import ColumnChart from '@/components/cards/models/ColumnChart';
import channelModel from '@/mixins/channel-model';
import cardsModel from '@/mixins/cards-model';
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import { getDiffInRangeDates } from '@/utils/date';
import { DATE_TIME } from '@/utils/constants';

export default {
  name: 'FanBaseEvolutionChart',
  components: {
    ColumnChart,
  },
  mixins: [
    channelModel,
    cardsModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: [],
      classifiedPercentage: 0,
    };
  },
  computed: {
    ...mapGetters('dashboard', [
      'activeDashboard',
    ]),
    cardOptions() {
      return {
        xAxis: {
          lineWidth: 0,
          tickLength: 0,
          type: 'datetime',
          labels: {
            format: this.labelFormat,
          },
        },
        yAxis: {
          min: null,
          plotLines: [{
            color: '#515156',
            width: 1,
            value: 0,
          }],
          labels: {
            enabled: true,
          },
        },
        legend: {
          enabled: true,
        },
        tooltip: {
          xDateFormat: this.xDateFormat,
        },
      };
    },
    categories() {
      const data = this.rawSeries?.[0]?.value?.results;

      const fanBase = data?.filter(typeOfData => typeOfData.name === 'FanBase')[0];

      const categories = fanBase?.data?.map(({ date }) => date);
      categories?.shift();

      return categories?.map(date => parseInt(moment(date).format('x'), 10));
    },
    series() {
      const data = this.rawSeries
        ?.filter(({ status, value }) => status === 'fulfilled' && value.results);

      const fanBase = data?.map(({ value }) => {
        const { page_id: pageId, date_range: dateRange } = value.request_parameters;
        const page = this.pages.filter(page => page.id === pageId).shift();

        const { name, color } = this.getChannelColorNameAndIcon(page.channel);

        const channelAndUsername = `${name} - ${page.username}`;

        const hasCancelled = value.results.some(typeOfData => typeOfData.name === 'Cancelamentos');

        const dataSeries = {
          newFans: !hasCancelled
            ? value.results.filter(typeOfData => typeOfData.name === 'FanBase')[0]?.data
            : value.results.filter(typeOfData => typeOfData.name === 'Novos fãs')[0]?.data,
          cancelledFas: !hasCancelled
            ? null
            : value.results.filter(typeOfData => typeOfData.name === 'Cancelamentos')[0]?.data,
        };

        const series = this.setNewFansDataSeries(dataSeries, dateRange, hasCancelled);

        return {
          color,
          name: channelAndUsername,
          data: series,
        };
      });

      return fanBase;
    },
    pages() {
      return this.activeDashboard.data.pages;
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getPerformancePageMetricsService',
    ]),
    setNewFansDataSeries(dataSeries, dateRange, hasCancelled) {
      /*
        There is a specific channel and page that always brings a day more than was requested
        Such as Facebook - owltripsop
        The startDay is used to normalize with the data of what was requested
      */
      const series = [];

      const diffDate = getDiffInRangeDates(dateRange, DATE_TIME.DAYS);

      const startDay = (dataSeries.newFans.length - diffDate);
      const endDay = this.categories.length > diffDate ? this.categories.length : this.categories.length + 1;

      for (let day = startDay; day < endDay; day++) {
        const dates = {
          previous: !hasCancelled ? dataSeries.newFans[day - 1].value : dataSeries.cancelledFas[day].value,
          current: dataSeries.newFans[day].value,
        };
        series.push({
          x: new Date(dataSeries.newFans[day].date).getTime(),
          y: dates.current - dates.previous,
        });
      }

      return series;
    },
    async getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      await this.getPerformancePageMetricsService({
        itemId: this.itemId,
        servicePath: 'metricsFanEvolution',
        usePreviousDay: true,
        params: {
          datePortion: this.getDatePortion(options),
        },
      })
        .then(response => {
          this.rawSeries = response
            ?.filter(data => data.status === 'fulfilled' && !data.value.status);
          this.requestStatus = 'success';
        })
        .catch(error => {
          console.error(error);
          this.requestStatus = 'error';
        });
    },
  },
};
</script>
